import React from 'react';
import { 
  AlertTriangle,
  Wrench,
  ArrowUp,
  Eye,
  Sparkles, 
  ImageIcon,
  AlertCircle,
  Info,
  ChevronDown
} from 'lucide-react';
import { 
  Accordion, 
  AccordionItem, 
  AccordionTrigger, 
  AccordionContent 
} from '../ui/accordion';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { InspectionEvaluationResponse, InspectionEvaluationIssue } from '../../models/inspectionEvaluationIssue';
import InspectionProcessingMetadata from '../InspectionEvaluationComponents/InspectionProcessingMetadata';

interface PropertyInspectionResultDisplayProps {
  inspectionEvaluation: InspectionEvaluationResponse;
  onCreateWorkItem: () => void;
  onOpenImageCarousel: (context: string[]) => void;
  showImages: boolean;
  showWorkItemPrompt: boolean;
}

const PropertyInspectionResultDisplay: React.FC<PropertyInspectionResultDisplayProps> = ({
  inspectionEvaluation,
  onCreateWorkItem,
  onOpenImageCarousel,
  showImages,
  showWorkItemPrompt
}) => {
  // Function to get category-specific icons and colors
  const getCategoryInfo = (category: string) => {
    switch (category) {
      case 'Safety':
        return {
          icon: <AlertTriangle className="h-5 w-5 mr-2 text-red-500" />,
          color: 'bg-red-100 text-red-800 border-red-200'
        };
      case 'Repair':
        return {
          icon: <Wrench className="h-5 w-5 mr-2 text-blue-500" />,
          color: 'bg-blue-100 text-blue-800 border-blue-200'
        };
      case 'Improvement':
        return {
          icon: <ArrowUp className="h-5 w-5 mr-2 text-green-500" />,
          color: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'Monitor':
        return {
          icon: <Eye className="h-5 w-5 mr-2 text-gray-500" />,
          color: 'bg-gray-100 text-gray-800 border-gray-200'
        };
      case 'Cleanliness':
        return {
          icon: <Sparkles className="h-5 w-5 mr-2 text-cyan-500" />,
          color: 'bg-cyan-100 text-cyan-800 border-cyan-200'
        };
      default:
        return {
          icon: <AlertTriangle className="h-5 w-5 mr-2 text-amber-500" />,
          color: 'bg-amber-100 text-amber-800 border-amber-200'
        };
    }
  };

  // Function to get severity color
  const getSeverityColor = (severity: number) => {
    if (severity > 7) return 'bg-red-100 text-red-800 border-red-200';
    if (severity > 4) return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Main render
  return (
    <div>
      {/* Synthesis Metadata - Collapsible section */}
      {inspectionEvaluation._synthesisMetadata && (
        <div className="mb-6">
          <details className="group">
            <summary className="cursor-pointer list-none flex justify-between items-center p-2 rounded-md bg-gray-50 hover:bg-gray-100">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Processing Information</span>
                
                {/* Show a badge if there are issues */}
                {!inspectionEvaluation._synthesisMetadata.processingSuccess && (
                  <span className="text-xs bg-amber-100 text-amber-800 py-0.5 px-2 rounded-full">
                    Processed with issues
                  </span>
                )}
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500 transition-transform group-open:rotate-180" />
            </summary>
            
            <div className="p-3 mt-1 border rounded-md bg-white">
              <InspectionProcessingMetadata 
                metadata={inspectionEvaluation._synthesisMetadata} 
              />
              
              {/* Show error alert if there was a processing error */}
              {!inspectionEvaluation._synthesisMetadata.processingSuccess && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-1">Inspection Processing Note</h4>
                    <p className="text-sm text-amber-700">
                      This inspection report encountered some processing issues. 
                      {inspectionEvaluation._synthesisMetadata.processingError 
                        ? ` Error details: ${inspectionEvaluation._synthesisMetadata.processingError}`
                        : ' The available results are shown below, but may be incomplete.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </details>
        </div>
      )}
      
      {/* Issues Accordion - No separate header, will be controlled by parent */}
      <Accordion type="single" collapsible defaultValue="issues">
        <AccordionItem value="issues" className="border-none">
          <AccordionTrigger className="py-3 px-4 bg-gray-50 rounded-md">
            <span className="text-lg font-medium">Details</span>
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-4">
              {inspectionEvaluation.issues?.map((issue: InspectionEvaluationIssue) => {
                const categoryInfo = getCategoryInfo(issue.category);
                const severityColor = getSeverityColor(issue.severity);
                
                return (
                  <Card key={issue.id} className="border border-gray-200 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <div className="flex items-center">
                          {categoryInfo.icon}
                          <h3 className="font-medium text-base">
                            {issue.shortDescription}
                          </h3>
                        </div>
                        
                        <div className="flex gap-2">
                          <Badge className={`${severityColor} font-normal`}>
                            Severity: {issue.severity}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <span className="text-sm font-medium">Location: </span>
                        <span className="text-sm">{issue.location}</span>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-4">
                        {issue.longDescription}
                      </p>
                      
                      {/* Only show "View Images" button if there are actually images to view */}
                      {showImages && issue.context?.length > 0 && Array.isArray(issue.context) && 
                       issue.context.some(item => typeof item === 'string' && item.trim() !== '') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenImageCarousel(issue.context)}
                          className="flex items-center gap-2"
                        >
                          <ImageIcon className="h-4 w-4" />
                          <span>View Images ({issue.context.filter(item => typeof item === 'string' && item.trim() !== '').length})</span>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Recommended Actions */}
        {inspectionEvaluation.recommendedActions && inspectionEvaluation.recommendedActions.length > 0 && (
          <AccordionItem value="actions" className="border-none mt-4">
            <AccordionTrigger className="py-3 px-4 bg-gray-50 rounded-md">
              <span className="text-lg font-medium">Recommended Actions</span>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              <ul className="space-y-2 pl-5 list-disc">
                {inspectionEvaluation.recommendedActions.map((action, index) => (
                  <li key={index} className="text-gray-700 pl-2">
                    {action}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Create Work Item Button */}
      {showWorkItemPrompt && (
        <div className="text-center mt-8">
          <Button
            onClick={onCreateWorkItem}
            className="font-medium"
          >
            Create Work Item
          </Button>
        </div>
      )}
    </div>
  );
};

export default PropertyInspectionResultDisplay;
